import { API_CONFIG, fetchAPI } from './api';

export type AdminUser = {
  email: string;
  accountName?: string | null;
  name?: string | null;
  company?: string | null;
  credits: number;
  contactCredits?: number;
  contactCreditsResetAt?: string;
  dailyCreditsLimit?: number | null;
  dailyContactCreditsLimit?: number | null;
  isAdmin: boolean;
  userType?: 'user' | 'admin' | 'free-trial';
  timezone: string;
  disabled: boolean;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  scheduleCount: number;
  webhook?: string | null;
  ghlPit?: string | null;
  ghlLocationId?: string | null;
  ghlSendAsLocationId?: string | null;
  ghlAssignedLocationIds?: string[];
  instantlyCampaignIds?: string[];
  linkedinConnectionCookie?: string | null;
  instagramConnectionCookie?: string | null;
  phantomBusterLinkedinCommentAgentId?: string | null;
  phantomBusterInstagramCommentAgentId?: string | null;
  pbConnectorIdLi?: string | null;
  pbConnectorIdIg?: string | null;
  features?: {
    customKpi?: boolean;
    contentScraping?: boolean;
    profitPod?: boolean;
    businessIntel?: boolean;
    pepaiResearch?: boolean;
    emailWorkspace?: boolean;
  };
};

export type GhlAccountRecord = {
  locationId: string;
  pit: string;
  accountName: string;
  email?: string | null;
  active?: boolean;
};

export type CreditUsageLicense = {
  id: string;
  email: string;
  active: boolean;
  credits24h: number;
  creditsMonthToDate: number;
  creditsAllTime: number;
  creditsSinceResetUtc: number;
  remainingTodayUtc: number;
};

export type CreditUsageUser = {
  email: string;
  credits24h: number;
  creditsMonthToDate: number;
  creditsAllTime: number;
  seamlessCredits24h: number;
  seamlessCreditsMonthToDate: number;
  seamlessCreditsAllTime: number;
};

export type CreditUsageSummary = {
  generatedAt: string;
  calendarDayHistoryUtc: Array<{
    dateUtc: string;
    creditsTotal: number;
    creditsSeamless: number;
    creditsApollo: number;
    eventsTotal: number;
    eventsSeamless: number;
    eventsApollo: number;
  }>;
  seamless: {
    credits24h: number;
    creditsMonthToDate: number;
    creditsAllTime: number;
    creditsSinceResetUtc: number;
    dailyCreditLimit: number;
    remainingTodayUtc: number;
    resetAtUtc: string;
    chargeEventsMonthToDate: number;
    uniqueEntitiesMonthToDate: number;
    repeatChargesMonthToDate: number;
    bySourceMonthToDate: Array<{
      source: string;
      creditsMonthToDate: number;
      eventsMonthToDate: number;
    }>;
    byTriggerReasonMonthToDate: Array<{
      triggerReason: string;
      creditsMonthToDate: number;
      eventsMonthToDate: number;
    }>;
    licenses: CreditUsageLicense[];
  };
  apollo: {
    credits24h: number;
    creditsMonthToDate: number;
    monthlyCreditCap: number;
    remainingThisMonth: number;
  };
  users: CreditUsageUser[];
};

export type WebsiteLookup = {
  companyName?: string;
  title?: string;
  fullName?: string;
  seniority?: string;
  department?: string;
  industry?: string;
  employeeSize?: string;
};

export type WebsiteRequest = {
  requestId: string;
  createdAt?: string;
  status?: string;
  sourceType?: 'website' | 'test_drive';
  requestName?: string | null;
  requestCompany?: string | null;
  requestTitle?: string | null;
  requesterEmail?: string;
  lookups?: WebsiteLookup[];
  emailSentDate?: string;
  pdfUrl?: string;
  reportSentDate?: string;
  reportPdfUrl?: string;
  searches?: Array<Record<string, any>>;
  reports?: Array<Record<string, any>>;
  [key: string]: any;
};

export type InstantlyCampaignMetric = {
  campaign_id: string;
  campaign_name?: string | null;
  sends: number;
  opens: number;
  clicks: number;
  replies: number;
  bounces: number;
  unsubscribes: number;
  complaints: number;
  failed: number;
  other: number;
  total: number;
  unique_leads?: number;
  unique_opens?: number;
  unique_clicks?: number;
  unique_replies?: number;
};

export type InstantlyDayMetric = {
  day: string;
  sends: number;
  opens: number;
  clicks: number;
  replies: number;
  bounces: number;
  unsubscribes: number;
  complaints: number;
  failed: number;
  other: number;
  total: number;
  unique_leads?: number;
  unique_opens?: number;
  unique_clicks?: number;
  unique_replies?: number;
};

export type InstantlySenderMetric = {
  sender_email: string;
  sends: number;
  opens: number;
  clicks: number;
  replies: number;
  bounces: number;
  unsubscribes: number;
  complaints: number;
  failed: number;
  other: number;
  total: number;
};

export type InstantlyPositiveReply = {
  reply_key: string;
  campaign_id: string;
  campaign_name?: string | null;
  lead_email: string;
  lead_name: string;
  sender_email?: string | null;
  reply_count: number;
  lt_interest_status: number;
  last_reply_at?: string | null;
  message_text?: string | null;
  note?: string | null;
  starred?: boolean;
  is_deleted?: boolean;
  updated_at?: string | null;
  created_at?: string | null;
};

export type InstantlyMetricsResponse = {
  success: boolean;
  filters?: {
    userEmail?: string | null;
    campaignId?: string | null;
    senderEmail?: string | null;
    start?: string;
    end?: string;
    limit?: number;
  };
  sampleSize?: number;
  totals?: {
    sends: number;
    opens: number;
    clicks: number;
    replies: number;
    bounces: number;
    unsubscribes: number;
    complaints: number;
    failed: number;
    other: number;
    total: number;
  };
  uniqueTotals?: {
    unique_leads: number;
    unique_opens: number;
    unique_clicks: number;
    unique_replies: number;
  };
  byCampaign?: InstantlyCampaignMetric[];
  bySender?: InstantlySenderMetric[];
  byDay?: InstantlyDayMetric[];
  positiveReplies?: InstantlyPositiveReply[];
  error?: string;
};

type AdminRequestBase = {
  requesterEmail: string;
  requesterPassword?: string;
  token?: string;
  baseUrl?: string;
};

function usersApi(baseUrl?: string) {
  return baseUrl || API_CONFIG.USERS_API;
}

function researchApi(baseUrl?: string) {
  return baseUrl || API_CONFIG.RESEARCH_API;
}

export async function getUserAsAdmin(input: AdminRequestBase & { targetEmail: string }) {
  return fetchAPI<{ success: boolean; user?: AdminUser; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'get_user',
      ...input,
    }),
  });
}

export async function createUserAsAdmin(
  input: AdminRequestBase & {
    targetEmail: string;
    password?: string;
    credits?: number;
    timezone?: string;
    isAdmin?: boolean;
    userType?: 'user' | 'admin' | 'free-trial';
    trialSurveyOptIn?: boolean;
    trialNewsletterOptIn?: boolean;
    disabled?: boolean;
  }
) {
  return fetchAPI<{ success: boolean; user?: AdminUser; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'create_user',
      ...input,
    }),
  });
}

export async function addCreditsAsAdmin(
  input: AdminRequestBase & { targetEmail: string; amount: number; creditType?: 'scrape' | 'contact' }
) {
  return fetchAPI<{ success: boolean; email?: string; credits?: number; contactCredits?: number; error?: string }>(
    usersApi(input.baseUrl),
    {
      method: 'POST',
      headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
      body: JSON.stringify({
        action: 'add_credits',
        ...input,
      }),
    }
  );
}

export async function setCreditsAsAdmin(
  input: AdminRequestBase & { targetEmail: string; amount: number; creditType?: 'scrape' | 'contact' }
) {
  return fetchAPI<{ success: boolean; email?: string; credits?: number; contactCredits?: number; error?: string }>(
    usersApi(input.baseUrl),
    {
      method: 'POST',
      headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
      body: JSON.stringify({
        action: 'set_credits',
        ...input,
      }),
    }
  );
}

export async function setDailyLimitsAsAdmin(
  input: AdminRequestBase & {
    targetEmail: string;
    dailyCreditsLimit: number | null;
    dailyContactCreditsLimit: number | null;
  }
) {
  return fetchAPI<{
    success: boolean;
    email?: string;
    dailyCreditsLimit?: number | null;
    dailyContactCreditsLimit?: number | null;
    error?: string;
  }>(
    usersApi(input.baseUrl),
    {
      method: 'POST',
      headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
      body: JSON.stringify({
        action: 'set_daily_limits',
        ...input,
      }),
    }
  );
}

export async function setPasswordAsAdmin(input: AdminRequestBase & { targetEmail: string; newPassword: string }) {
  return fetchAPI<{ success: boolean; email?: string; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'set_password',
      ...input,
    }),
  });
}

export async function setDisabledAsAdmin(input: AdminRequestBase & { targetEmail: string; disabled: boolean }) {
  return fetchAPI<{ success: boolean; email?: string; disabled?: boolean; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'set_disabled',
      ...input,
    }),
  });
}

export async function setWebhookAsAdmin(input: AdminRequestBase & { targetEmail: string; webhook: string | null }) {
  return fetchAPI<{ success: boolean; email?: string; webhook?: string | null; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'set_webhook',
      ...input,
    }),
  });
}

export async function setGhlConfigAsAdmin(
  input: AdminRequestBase & {
    targetEmail: string;
    pit: string | null;
    locationId: string | null;
    linkedinConnectionCookie?: string | null;
    instagramConnectionCookie?: string | null;
    phantomBusterLinkedinCommentAgentId?: string | null;
    phantomBusterInstagramCommentAgentId?: string | null;
    pbConnectorIdLi?: string | null;
    pbConnectorIdIg?: string | null;
  }
) {
  return fetchAPI<{
    success: boolean;
    email?: string;
    ghlPit?: string | null;
    ghlLocationId?: string | null;
    linkedinConnectionCookie?: string | null;
    instagramConnectionCookie?: string | null;
    phantomBusterLinkedinCommentAgentId?: string | null;
    phantomBusterInstagramCommentAgentId?: string | null;
    pbConnectorIdLi?: string | null;
    pbConnectorIdIg?: string | null;
    error?: string;
  }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'set_ghl_config',
      ...input,
    }),
  });
}

export async function setGhlLocationAssignmentsAsAdmin(
  input: AdminRequestBase & {
    targetEmail: string;
    ghlAssignedLocationIds: string[];
    ghlSendAsLocationId?: string | null;
  }
) {
  return fetchAPI<{
    success: boolean;
    email?: string;
    ghlAssignedLocationIds?: string[];
    ghlSendAsLocationId?: string | null;
    error?: string;
  }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'set_ghl_location_assignments',
      ...input,
    }),
  });
}

export async function setFeaturesAsAdmin(
  input: AdminRequestBase & {
    targetEmail: string;
    features: {
      customKpi?: boolean;
      contentScraping?: boolean;
      profitPod?: boolean;
      businessIntel?: boolean;
      pepaiResearch?: boolean;
      emailWorkspace?: boolean;
    };
  }
) {
  return fetchAPI<{ success: boolean; email?: string; features?: AdminUser['features']; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'set_features',
      ...input,
    }),
  });
}

export async function setInstantlyCampaignAssignmentsAsAdmin(
  input: AdminRequestBase & {
    targetEmail: string;
    instantlyCampaignIds: string[];
  }
) {
  return fetchAPI<{
    success: boolean;
    email?: string;
    instantlyCampaignIds?: string[];
    error?: string;
  }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'set_instantly_campaign_assignments',
      ...input,
    }),
  });
}

export async function setUserTypeAsAdmin(
  input: AdminRequestBase & {
    targetEmail: string;
    userType: 'user' | 'admin' | 'free-trial';
  }
) {
  return fetchAPI<{ success: boolean; userType?: AdminUser['userType']; features?: AdminUser['features']; error?: string }>(
    usersApi(input.baseUrl),
    {
      method: 'POST',
      headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
      body: JSON.stringify({
        action: 'set_user_type',
        ...input,
      }),
    }
  );
}

export async function getActivityAsAdmin(input: AdminRequestBase & { targetEmail: string; limit?: number }) {
  return fetchAPI<{ success: boolean; items?: any[]; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'get_activity',
      ...input,
    }),
  });
}

export async function getCreditUsageAsAdmin(input: AdminRequestBase) {
  return fetchAPI<{ success: boolean; usage?: CreditUsageSummary; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'get_credit_usage',
      ...input,
    }),
  });
}

export async function listUsersAsAdmin(input: AdminRequestBase & { limit?: number; startKey?: any }) {
  return fetchAPI<{ success: boolean; users?: AdminUser[]; nextKey?: any; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'list_users',
      ...input,
    }),
  });
}

export async function listGhlAccountsAsAdmin(input: AdminRequestBase) {
  return fetchAPI<{ success: boolean; items?: GhlAccountRecord[]; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'list_ghl_accounts',
      ...input,
    }),
  });
}

export async function upsertGhlAccountAsAdmin(
  input: AdminRequestBase & {
    locationId: string;
    pit: string;
    accountName: string;
    email?: string | null;
    active?: boolean;
  }
) {
  return fetchAPI<{ success: boolean; item?: GhlAccountRecord; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'upsert_ghl_account',
      ...input,
    }),
  });
}

export async function testGhlAccountAsAdmin(
  input: AdminRequestBase & {
    locationId: string;
    pit?: string;
    contactName?: string;
    email?: string;
    companyName?: string;
  }
) {
  return fetchAPI<{
    success: boolean;
    statusCode?: number;
    requestPayload?: Record<string, unknown>;
    response?: unknown;
    error?: string;
  }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'test_ghl_account',
      ...input,
    }),
  });
}

export async function provisionGhlCustomFieldsAsAdmin(
  input: AdminRequestBase & {
    locationId: string;
    pit?: string;
    fieldNames: string[];
  }
) {
  return fetchAPI<{
    success: boolean;
    locationId?: string;
    totalRequested?: number;
    created?: Array<{ name: string; fieldKey?: string; id?: string }>;
    skipped?: Array<{ name: string; fieldKey?: string; id?: string }>;
    failed?: Array<{ name: string; error: string }>;
    error?: string;
  }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'provision_ghl_custom_fields',
      ...input,
    }),
  });
}

export async function listWebhookInboundLogsAsAdmin(input: AdminRequestBase & { limit?: number; startKey?: any }) {
  return fetchAPI<{ success: boolean; items?: any[]; nextKey?: any; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'list_webhook_inbound',
      ...input,
    }),
  });
}

export async function listWebhookOutboundLogsAsAdmin(input: AdminRequestBase & { limit?: number; startKey?: any }) {
  return fetchAPI<{ success: boolean; items?: any[]; nextKey?: any; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'list_webhook_outbound',
      ...input,
    }),
  });
}

export async function listErrorLogsAsAdmin(input: AdminRequestBase & { limit?: number; startKey?: any }) {
  return fetchAPI<{ success: boolean; items?: any[]; nextKey?: any; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'list_error_logs',
      ...input,
    }),
  });
}

export async function listSchedulesAsAdmin(
  input: AdminRequestBase & { limit?: number; startKey?: any; filterEmail?: string }
) {
  return fetchAPI<{ success: boolean; items?: any[]; nextKey?: any; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'list_schedules',
      ...input,
    }),
  });
}

export async function listScheduleRunsAsAdmin(
  input: AdminRequestBase & { limit?: number; startKey?: any; filterEmail?: string; status?: string }
) {
  return fetchAPI<{ success: boolean; items?: any[]; nextKey?: any; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'list_schedule_runs',
      ...input,
    }),
  });
}

export async function updateScheduleAsAdmin(
  input: AdminRequestBase & {
    userEmail: string;
    scheduleId: string;
    updates: Record<string, any>;
  }
) {
  return fetchAPI<{ success: boolean; item?: any; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'update_schedule',
      ...input,
    }),
  });
}

export async function deleteScheduleAsAdmin(
  input: AdminRequestBase & {
    userEmail: string;
    scheduleId: string;
  }
) {
  return fetchAPI<{ success: boolean; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'delete_schedule',
      ...input,
    }),
  });
}

export async function listWebsiteRequests(
  input: AdminRequestBase & { limit?: number; startKey?: any; filterEmail?: string }
) {
  return fetchAPI<{ success: boolean; items?: WebsiteRequest[]; nextKey?: any; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'list_website_requests',
      ...input,
    }),
  });
}

export async function updateWebsiteRequest(
  input: AdminRequestBase & {
    requestId: string;
    emailSentDate?: string | null;
    pdfUrl?: string | null;
  }
) {
  return fetchAPI<{ success: boolean; item?: WebsiteRequest; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'update_website_request',
      ...input,
    }),
  });
}

export async function listTestDriveRequests(
  input: AdminRequestBase & { limit?: number; startKey?: any; filterEmail?: string }
) {
  return fetchAPI<{ success: boolean; items?: WebsiteRequest[]; nextKey?: any; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'list_test_drive_requests',
      ...input,
    }),
  });
}

export async function updateTestDriveRequest(
  input: AdminRequestBase & {
    requestId: string;
    reportSentDate?: string | null;
    reportPdfUrl?: string | null;
  }
) {
  return fetchAPI<{ success: boolean; item?: WebsiteRequest; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      action: 'update_test_drive_request',
      ...input,
    }),
  });
}

export async function listInstantlyMetricsAsAdmin(
  input: AdminRequestBase & {
    userEmail?: string;
    campaignId?: string;
    senderEmail?: string;
    start?: string;
    end?: string;
    limit?: number;
  }
) {
  const params = new URLSearchParams();
  if (input.userEmail) params.set('userEmail', input.userEmail.trim());
  if (input.campaignId) params.set('campaignId', input.campaignId.trim());
  if (input.senderEmail) params.set('senderEmail', input.senderEmail.trim());
  if (input.start) params.set('start', input.start);
  if (input.end) params.set('end', input.end);
  if (input.limit != null) params.set('limit', String(input.limit));
  const url = `${researchApi(input.baseUrl).replace(/\/$/, '')}/instantly-events/metrics?${params.toString()}`;
  return fetchAPI<InstantlyMetricsResponse>(url, {
    method: 'GET',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
  });
}

export async function syncInstantlyMetricsAsAdmin(
  input: AdminRequestBase & {
    userEmail?: string;
    search?: string;
    maxCampaigns?: number;
    maxPagesPerCampaign?: number;
    leadsPageSize?: number;
  }
) {
  const url = `${researchApi(input.baseUrl).replace(/\/$/, '')}/instantly-events/sync`;
  return fetchAPI<{
    success: boolean;
    synced?: boolean;
    campaignsProcessed?: number;
    leadsProcessed?: number;
    rowsUpserted?: number;
    error?: string;
  }>(url, {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      userEmail: input.userEmail,
      search: input.search,
      maxCampaigns: input.maxCampaigns,
      maxPagesPerCampaign: input.maxPagesPerCampaign,
      leadsPageSize: input.leadsPageSize,
    }),
  });
}

export async function upsertInstantlyReplyAsAdmin(
  input: AdminRequestBase & {
    reply_key?: string;
    campaign_id?: string;
    campaign_name?: string | null;
    lead_email: string;
    lead_name?: string;
    sender_email?: string | null;
    reply_count?: number;
    lt_interest_status?: number;
    last_reply_at?: string | null;
    message_text?: string | null;
    note?: string | null;
    starred?: boolean;
    is_deleted?: boolean;
  }
) {
  const url = `${researchApi(input.baseUrl).replace(/\/$/, '')}/instantly-events/replies/upsert`;
  return fetchAPI<{ success: boolean; row?: InstantlyPositiveReply; error?: string }>(url, {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      reply_key: input.reply_key,
      campaign_id: input.campaign_id,
      campaign_name: input.campaign_name,
      lead_email: input.lead_email,
      lead_name: input.lead_name,
      sender_email: input.sender_email,
      reply_count: input.reply_count,
      lt_interest_status: input.lt_interest_status,
      last_reply_at: input.last_reply_at,
      message_text: input.message_text,
      note: input.note,
      starred: input.starred,
      is_deleted: input.is_deleted,
    }),
  });
}

export async function deleteInstantlyReplyAsAdmin(
  input: AdminRequestBase & {
    reply_key?: string;
    lead_email?: string;
    sender_email?: string | null;
  }
) {
  const url = `${researchApi(input.baseUrl).replace(/\/$/, '')}/instantly-events/replies/delete`;
  return fetchAPI<{ success: boolean; reply_key?: string; error?: string }>(url, {
    method: 'POST',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : undefined,
    body: JSON.stringify({
      reply_key: input.reply_key,
      lead_email: input.lead_email,
      sender_email: input.sender_email,
    }),
  });
}
